// ** Modules
import { useId } from 'react';

// ** UI
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// ** Types
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';
import type { DashboardLink, DashboardProfileItem, DashboardProfileItemIcon, DashboardProfileSection } from './types';

// ** Components
import Link from '@/components/common/Link';

type DashboardPanelProps = {
	title: string;
	cta?: DashboardLink;
	children: ReactNode;
	compactOnDesktop?: boolean;
	sx?: SxProps<Theme>;
};

type DashboardBodyTextProps = {
	children: ReactNode;
	variant?: TypographyProps['variant'];
	component?: TypographyProps['component'];
	sx?: SxProps<Theme>;
};

export const DashboardBodyText = ({ children, variant = 'body-md', component = 'p', sx }: DashboardBodyTextProps) => {
	return (
		<Typography
			variant={variant}
			component={component}
			sx={[{ m: 0, color: 'common.black' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
		>
			{children}
		</Typography>
	);
};

export const DashboardProfileValue = ({ value }: { value: DashboardProfileItem['value'] }) => {
	const lines = Array.isArray(value) ? value : [value];
	const lineCounts = new Map<string, number>();
	const keyedLines = lines.map((line) => {
		const occurrence = (lineCounts.get(line) ?? 0) + 1;
		lineCounts.set(line, occurrence);

		return { key: `${line}-${occurrence}`, line };
	});

	return (
		<>
			{keyedLines.map(({ key, line }) => (
				<Box key={key} component="span" sx={{ display: 'block' }}>
					{line}
				</Box>
			))}
		</>
	);
};

export const DashboardCtaLink = ({ cta, sx }: { cta: DashboardLink; sx?: SxProps<Theme> }) => {
	return (
		<Box
			sx={[
				{
					width: { xs: '100%', md: 'auto' },
					textAlign: { xs: 'left', md: 'right' },
					'& a': {
						display: 'inline-block',
						color: 'text.secondary',
						textDecoration: 'underline',
						textDecorationSkipInk: 'none',
						textUnderlineOffset: '2px',
						textDecorationThickness: '1px',
						transition: 'opacity 180ms ease, text-underline-offset 180ms ease, text-decoration-thickness 180ms ease'
					},
					'& a:hover, & a:focus-visible': {
						color: 'text.secondary',
						opacity: 0.72,
						textUnderlineOffset: '3px',
						textDecorationThickness: '2px'
					}
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			<Link href={cta.href} passHref style={{ textDecoration: 'underline' }}>
				<Typography variant="body-md" component="span" sx={{ color: 'text.secondary', textDecoration: 'inherit' }}>
					{cta.label}
				</Typography>
			</Link>
		</Box>
	);
};

export const DashboardPanel = ({ title, cta, children, compactOnDesktop = false, sx }: DashboardPanelProps) => {
	const headingId = useId();

	return (
		<Box
			component="section"
			aria-labelledby={headingId}
			sx={[
				{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: compactOnDesktop ? { md: 'center' } : undefined,
					minWidth: 0,
					backgroundColor: 'common.white',
					borderRadius: '8px',
					p: { xs: 4, md: 8 }
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					alignItems: { xs: 'flex-start', md: 'center' },
					justifyContent: 'space-between',
					gap: { xs: 2, md: 4 },
					mb: compactOnDesktop ? { xs: 8, md: 0 } : 8
				}}
			>
				<Typography id={headingId} variant="panel-heading" component="h2" sx={{ color: 'common.black' }}>
					{title}
				</Typography>

				{cta ? <DashboardCtaLink cta={cta} /> : null}
			</Box>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					flex: '1 1 auto',
					minHeight: 0
				}}
			>
				{children}
			</Box>
		</Box>
	);
};

export const getProfileSummaryLines = (profile: DashboardProfileSection) => {
	if (profile.summaryLines?.length) {
		return profile.summaryLines;
	}

	return profile.items
		.filter((item) => item.icon === 'address' || item.icon === 'email')
		.flatMap((item) => (Array.isArray(item.value) ? item.value : [item.value]))
		.slice(0, 3);
};

export const getProfileIcon = (icon: DashboardProfileItemIcon) => {
	switch (icon) {
		case 'person':
			return <PersonOutlinedIcon fontSize="small" aria-hidden="true" />;
		case 'address':
			return <HomeOutlinedIcon fontSize="small" aria-hidden="true" />;
		case 'email':
			return <MailOutlinedIcon fontSize="small" aria-hidden="true" />;
		case 'mobile':
			return <SmartphoneOutlinedIcon fontSize="small" aria-hidden="true" />;
		case 'phone':
			return <LocalPhoneOutlinedIcon fontSize="small" aria-hidden="true" />;
		default:
			return null;
	}
};
